import type { SpecType } from "@/api/contracts/specifications";

// The details page's lines body — its groups, rows and cells — as pure rules
// over the lines page (#695). The Editor (#548) draws its own rows over the
// same rules, so none of them knows which page it is on.
//
// **The shapes are declared here rather than imported from the wire face**, as
// in `@/lib/spec-display`: the client graph may never import `@/server/*`. Each
// is the narrow slice a rule reads, so the lines page satisfies it
// structurally.
//
// Every string and condition is the donor's; the parity enumeration on #695
// names the source line of each.

/** `/^Selection \d+$/` — the name a selection is created with (`common.ts:358-364`). */
const DEFAULT_SELECTION_NAME = /^Selection \d+$/;

/**
 * A single-selection row's name — `getLineName`: the line's name alone while
 * the selection keeps its default name, else `<line> - (<selection>)`.
 *
 * A row over no selection reads the line's name alone. The donor's template
 * prints it as "<line> - (undefined)" (`LineDetails.tsx:31-36`, every
 * selection of a line deleted); Atlas drops that leak, a named parity break
 * (owner disposition D9 = c on PR #1106). A `null` name still prints as the
 * template prints it.
 */
export function lineDisplayName(lineName: string, selectionName: string | null | undefined) {
  return selectionName === undefined || DEFAULT_SELECTION_NAME.test(String(selectionName))
    ? lineName
    : `${lineName} - (${selectionName})`;
}

/** A line's location type — the wire's `SPEC_LINE_LOCATION_TYPES`. */
export type LineLocationType = "applicable" | "per_plan" | "specific";

/** `LocationTypeMapping` (`common.ts:213-217`); `specific` never reads its entry. */
const LOCATION_TYPE_TEXT: Record<Exclude<LineLocationType, "specific">, string> = {
  applicable: "Applicable Locations",
  per_plan: "Per Plan",
};

/**
 * The locations line under a line's name — `getLocationNames`
 * (`shared/utils.ts:8-14`).
 */
export function locationsText(
  locationType: LineLocationType,
  locations: readonly { name: string }[],
): string {
  if (locationType === "specific") {
    return locations.map((location) => location.name).join(", ") || "0 Specific locations";
  }
  // The donor's fallback, for a value the wire's enum does not have.
  return LOCATION_TYPE_TEXT[locationType] ?? "Unknown location type";
}

/**
 * A line's placements whose room is still in the version's room list — the
 * Editor's locations select's `selectedLineLocations`
 * (`LineLocations.tsx:53-58`): only these are checked, and only these count
 * in the closed label, so a room deleted elsewhere never inflates it.
 */
export function checkedPlacements<P extends { locationId: number }>(
  placements: readonly P[],
  rooms: readonly { id: number }[],
): P[] {
  const listed = new Set(rooms.map((room) => room.id));
  return placements.filter((placement) => listed.has(placement.locationId));
}

/**
 * The Editor's locations select, closed — `getDropdownText`
 * (`LineLocations.tsx:507-524`): the two other types by name; a `specific`
 * line's one checked room by its name as stored, any other count as
 * `${n} Locations` — `0 Locations` included.
 */
export function locationsSelectText(
  locationType: LineLocationType,
  checked: readonly { name: string }[],
): string {
  if (locationType !== "specific") return LOCATION_TYPE_TEXT[locationType];
  return checked.length === 1 ? checked[0]!.name : `${checked.length} Locations`;
}

/**
 * Whether the toolbar offers "Show changes from Base" — `SwitchChanges.tsx:22`:
 * not on a core spec, and not without a Parent version (the donor's
 * `basedOnSpecId`, which #693 carries as the summary's `parentVersion`).
 */
export function offersChangesSwitch(spec: { type: SpecType; parentVersion: object | null }) {
  return spec.type !== "core" && spec.parentVersion !== null;
}

/**
 * Whether the toolbar offers "Show Target Cost" — `SpecDetailsIsland.tsx:204`:
 * a community spec, to a specs editor (the donor's `isSpecsEditor`, which is
 * Atlas's `canEditSpecs`).
 */
export function offersTargetCostSwitch(spec: { type: SpecType }, canEditSpecs: boolean) {
  return canEditSpecs && spec.type === "community";
}

/** Which setting a target-cost switch drives (#1241 D1). */
export type TargetCostSetting = "targetCost" | "maxPrice";

/**
 * The editor's setting: a community spec's is target cost, the details
 * page's too; any other's is max price, its own (#1241 D1). The one rule the
 * switch's label follows, so the two cannot disagree.
 */
export function targetCostSwitchSetting(spec: { type: SpecType }): TargetCostSetting {
  return spec.type === "community" ? "targetCost" : "maxPrice";
}

/**
 * The target-cost switch's label — `switch-target-cost.tsx:28`: "Show Target
 * Cost" on a community spec, "Show Max Price" on the others. The details page
 * offers it on community specs only, so only the Editor reads the other half.
 */
export function targetCostSwitchLabel(spec: { type: SpecType }) {
  return targetCostSwitchSetting(spec) === "targetCost" ? "Show Target Cost" : "Show Max Price";
}

/** The facts the changed test reads. */
export interface ChangedFacts {
  changedFromBase: boolean;
}

/**
 * A row's base row is decided on the server (ADR-0021 decision 3): a
 * same-lineage row in the spec's current Parent version. The browser reads the
 * fact and never derives it.
 */
export interface BaseRowFacts extends ChangedFacts {
  hasBaseRow: boolean;
}

export type SelectionChangedFacts = BaseRowFacts;

/**
 * Whether a row differs from its Parent version — `isDifferentFromBase`
 * (`LineDetailsRow.tsx:121-122`): the line's server-computed flag, the
 * selection's, or — on a community spec — a selection with no base row, which
 * is one the community added. The donor tested its based-on pointer instead; by
 * lineage is a named parity break (#981 Q2). A missing selection reads as
 * having no base row, as the donor's falsy test reads it.
 */
export function isRowChanged(
  line: ChangedFacts,
  selection: SelectionChangedFacts | undefined,
  specType: SpecType,
): boolean {
  return (
    line.changedFromBase ||
    Boolean(selection?.changedFromBase) ||
    (specType === "community" && !selection?.hasBaseRow)
  );
}

export type RowBackground = "highlight" | "paid-upgrade" | "changed" | "none";

/**
 * A desktop row's ground (`LineDetailsRow.tsx:155-164`): the deep link's blue
 * over a paid upgrade's green over a change's orange.
 */
export function rowBackground(facts: {
  highlighted: boolean;
  paidUpgrade: boolean;
  changed: boolean;
}): RowBackground {
  if (facts.highlighted) return "highlight";
  if (facts.paidUpgrade) return "paid-upgrade";
  if (facts.changed) return "changed";
  return "none";
}

/**
 * The selections a line draws (`LineDetails.tsx:31`): all of them with "Show
 * changes from Base" on, the alive ones otherwise. More than one is a parent
 * row with child rows.
 */
export function shownSelections<T extends { deleted: boolean }>(
  selections: readonly T[],
  showChanges: boolean,
): T[] {
  return showChanges ? [...selections] : selections.filter((selection) => !selection.deleted);
}

/** One group as the display cursor weighs it. */
export interface CursorGroup {
  /** The server's count for the group. */
  lineCount: number;
  /** How many of its lines have arrived, deleted ones included. */
  loadedLines: number;
}

/**
 * How far down the groups the body draws — `selectGroupDisplayCursorById`
 * (`objectCache.ts:376-414`). The body draws groups `0..cursor` inclusive.
 *
 * It walks the **fetched** cursor (the offset the last page answered with,
 * lines the body later hides included) against the running line counts, so a
 * group appears once its first line can have arrived and the groups after it
 * wait for theirs — no header over rows still on their way. Every group shows
 * when the version has no lines at all, and once the cursor reaches the count,
 * which is what brings trailing empty groups in at the end. `hasMore` is never
 * read: the counts decide.
 *
 * A group's weight is the larger of its count and its loaded lines — the
 * donor's `max(existing, incoming, lineIds.length)` (`objectCache.ts:139-143`).
 */
export function groupDisplayCursor(
  groups: readonly CursorGroup[],
  cursor: number,
  count: number,
): number {
  if (cursor === 0 && count === 0) return groups.length;
  if (cursor >= count) return groups.length;
  let sum = 0;
  for (let index = 0; index < groups.length; index++) {
    const group = groups[index]!;
    sum += Math.max(group.lineCount, group.loadedLines);
    if (sum >= cursor) return index;
  }
  return groups.length;
}

/**
 * A deep-link id read the way the donor reads it: present when the raw param is
 * truthy, then `parseInt` with no radix (`SpecDetailsIsland.tsx:98-105`,
 * `LineDetailsRow.tsx:55`) — so `12abc` is 12, `0x1A` is 26, `1e3` is 1, and
 * `abc` is `NaN`, an id no line has, so the deep link looks and never lands
 * (#695 D1). An empty param is absent.
 */
export function deepLinkId(raw: string | undefined): number | undefined {
  return raw ? Number.parseInt(raw) : undefined;
}

/**
 * Where a new search term takes the URL — the donor's `setSearchParams(…, {
 * replace: true })`: the term in place of the history entry, the other params
 * (the deep link's included) kept, and an empty term dropped.
 */
export function specSearchNavigation(term: string) {
  return {
    search: <Search extends { specSearch?: string }>(previous: Search) => ({
      ...previous,
      specSearch: term || undefined,
    }),
    replace: true,
  };
}

/**
 * The hero box's two marks, one rule for the desktop row and the mobile rows
 * (`LineDetailsRow.tsx:141-146,268-271`): neither on a per-plan line; Core for
 * an approved core item; the EI logo for an approved item with EI tiers. An
 * unapproved item — the donor's `isPendingApproval` — shows neither.
 */
export function heroBadges(
  line: { perPlan: boolean },
  item: { isCore: boolean; isApproved: boolean; hasEiTiers: boolean } | null,
): { showCoreBadge: boolean; showEiBadge: boolean } {
  const approved = !line.perPlan && item !== null && item.isApproved;
  return {
    showCoreBadge: approved && item.isCore,
    showEiBadge: approved && item.hasEiTiers,
  };
}

// ---------------------------------------------------------------------------
// The Editor's rows (#809) — `EditableLine.tsx`, `LineContentRow.tsx` and
// `lineUtils.ts` in the donor's `SpecEditIsland/.../Lines/`. The parity
// enumeration on #809 names the source line of each rule. They sit beside the
// details page's and share none of its row rules: the two islands differ in
// the changed test, the name and the row kinds.
// ---------------------------------------------------------------------------

/** A row of the Editor's line block — `CONTENT_TYPE` (`constants.ts:1-6`). */
export type EditorRowKind = "LINE_ONLY" | "LINE_AND_SELECTION" | "SELECTION_ONLY";

/** What a removed note's row reads: the flagged fields, and the base's notes. */
interface RemovedNotesFacts {
  changedFields: readonly string[];
  base: { notes: string | null } | null;
}

/** What a line's removed rooms' notes read (#1106 D8). */
interface RemovedRoomsFacts {
  base: { notes: string | null; removedRooms: readonly { notes: string | null }[] } | null;
}

/** The facts the Editor's rows read off a selection. */
export interface EditorSelectionFacts extends SelectionChangedFacts, RemovedNotesFacts {
  id: number;
  deleted: boolean;
  notes: string | null;
}

/** The facts the Editor's rows read off a line. */
export interface EditorLineFacts<S extends EditorSelectionFacts = EditorSelectionFacts>
  extends ChangedFacts, RemovedRoomsFacts {
  changedFields: readonly string[];
  id: number;
  deleted: boolean;
  notes: string | null;
  locationType: LineLocationType;
  hasBaseRow: boolean;
  selections: readonly S[];
}

export type EditorRow<S> =
  | { kind: "LINE_ONLY"; selection: S }
  | { kind: "LINE_AND_SELECTION"; selection: S | undefined }
  | { kind: "SELECTION_ONLY"; selection: S }
  | { kind: "SELECTION_NOTES"; selection: S }
  | { kind: "LINE_NOTES" };

/**
 * Notes the base had and this row no longer has (#1104; #1060 Q7): the row's
 * `notes` flagged, the base's notes present, its own empty — by the notes
 * row's own truthiness gate.
 */
function hasRemovedNotes(row: RemovedNotesFacts & { notes: string | null }): boolean {
  return row.changedFields.includes("notes") && Boolean(row.base?.notes) && !row.notes;
}

/**
 * Rooms the line no longer has whose notes were set (#1106 D8): a line that
 * stopped being specific, or a specific line that lost a room.
 */
export function hasRemovedRoomNotes(
  line: RemovedRoomsFacts & { changedFields: readonly string[] },
): boolean {
  return (
    (line.changedFields.includes("rooms") || line.changedFields.includes("locationType")) &&
    Boolean(line.base?.removedRooms.some((room) => room.notes))
  );
}

/**
 * The rows a line block draws, in order (`EditableLine.tsx:54-111`): nothing
 * for a deleted line with the switch off; one `LINE_AND_SELECTION` row over the
 * one shown selection, or over none; with more, a `LINE_ONLY` header over the
 * first shown selection and, per shown selection, its `SELECTION_ONLY` row and
 * — when it has notes — its notes row; then the line's notes row, when it has
 * notes, and always on a `specific` line (its per-location notes table).
 *
 * With "Show changes from Base" on, a notes row the source would skip is drawn
 * for a **removed** note, so `~~old~~ ↳ None` can show (#1104; #1060 Q7), and
 * for a line's removed rooms that had notes (#1106 D8); and a single
 * selection's **changed** notes get a notes row under the line's — which the
 * source never draws (#1106 D7, a ruled parity break). Never on a struck row,
 * which is itself the change.
 */
export function editorRows<S extends EditorSelectionFacts>(
  line: EditorLineFacts<S>,
  showChanges: boolean,
): EditorRow<S>[] {
  if (line.deleted && !showChanges) return [];
  const shown = shownSelections(line.selections, showChanges);
  const removed = (row: RemovedNotesFacts & { notes: string | null; deleted: boolean }) =>
    showChanges && !line.deleted && !row.deleted && hasRemovedNotes(row);
  const rows: EditorRow<S>[] = [];
  if (shown.length > 1) {
    rows.push({ kind: "LINE_ONLY", selection: shown[0]! });
    for (const selection of shown) {
      rows.push({ kind: "SELECTION_ONLY", selection });
      if (selection.notes || removed(selection)) rows.push({ kind: "SELECTION_NOTES", selection });
    }
  } else {
    rows.push({ kind: "LINE_AND_SELECTION", selection: shown[0] });
  }
  const live = showChanges && !line.deleted;
  if (
    line.notes ||
    line.locationType === "specific" ||
    removed(line) ||
    (live && hasRemovedRoomNotes(line))
  ) {
    rows.push({ kind: "LINE_NOTES" });
  }
  const only = shown.length === 1 ? shown[0]! : undefined;
  if (live && only && !only.deleted && only.base && only.changedFields.includes("notes")) {
    rows.push({ kind: "SELECTION_NOTES", selection: only });
  }
  return rows;
}

/**
 * A line-level row's name (`LineContentRow.tsx:263-294`): `getLineName` on a
 * single row whose selection has a name, the line's name otherwise — so,
 * unlike the details rows, never `(undefined)`.
 */
export function editorLineName(
  kind: "LINE_ONLY" | "LINE_AND_SELECTION",
  lineName: string,
  selectionName: string | null | undefined,
): string {
  return kind === "LINE_AND_SELECTION" && selectionName
    ? lineDisplayName(lineName, selectionName)
    : lineName;
}

/** A selection row's name — the selection's, ` - Paid Upgrade` after it when flagged (`:276`). */
export function selectionRowName(selection: { name: string | null; isPaidUpgrade: boolean }) {
  return `${selection.name ?? ""}${selection.isPaidUpgrade ? " - Paid Upgrade" : ""}`;
}

/** `truncateText` (`common.ts:9-19`): the first `max` characters and `...` past them. */
export function truncateText(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

/** The item name's cut (`LineSelectionSummary.tsx`). */
export const ITEM_NAME_MAX = 50;
/** The model number's cut (`LineModelNumber.tsx`). */
export const MODEL_NUMBER_MAX = 25;

/**
 * Cell 5 (`LineModelNumber.tsx:22-44`): nothing on a per-plan line or with no
 * item; the model number cut at 25; with none, `--` on the Active selection's
 * row and `Model Number` elsewhere, both as the grey placeholder.
 */
export function modelNumberText(facts: {
  perPlan: boolean;
  item: { modelNumber: string | null } | null;
  isActive: boolean;
}): { text: string; placeholder: boolean } | null {
  if (facts.perPlan || !facts.item) return null;
  const { modelNumber } = facts.item;
  if (modelNumber) return { text: truncateText(modelNumber, MODEL_NUMBER_MAX), placeholder: false };
  return { text: facts.isActive ? "--" : "Model Number", placeholder: true };
}

/** A row for the class rules: the three drawn kinds, or a notes row. */
export type EditorStateKind = EditorRowKind | "NOTES";

/**
 * Whether an Editor row differs from its Parent version, by kind
 * (`lineUtils.ts:156-183`): a header row reads the line's flag and base row,
 * a single row both flags and the selection's base row, a selection row the
 * selection alone; a notes row never. The donor read its based-on pointers;
 * the server's `hasBaseRow` stands in for them, as in `isRowChanged`.
 */
export function isEditorRowChanged(
  kind: EditorStateKind,
  line: BaseRowFacts,
  selection: SelectionChangedFacts | undefined,
  specType: SpecType,
): boolean {
  const community = specType === "community";
  switch (kind) {
    case "LINE_ONLY":
      return line.changedFromBase || (community && !line.hasBaseRow);
    case "LINE_AND_SELECTION":
      return (
        line.changedFromBase ||
        Boolean(selection?.changedFromBase) ||
        (community && !selection?.hasBaseRow)
      );
    case "SELECTION_ONLY":
      return Boolean(selection?.changedFromBase) || (community && !selection?.hasBaseRow);
    case "NOTES":
      return false;
  }
}

/** The Active selection: a line and its selection, by id (`CONTEXT.md`). */
export interface ActiveRowIds {
  lineId: number;
  selectionId: number | null;
  /**
   * Set as Per Plan's emit (#861 D3 a): the panel reads the selection as having
   * no item — the donor's `itemId: null` — until the Active selection is next
   * set. The editor's rows never read it.
   */
  itemCleared?: boolean;
}

/** The named facts behind an Editor row's look — `getComputedRowClassNames`. */
export interface EditorRowState {
  /** `changed-from-base` on a community spec, `changed-from-core` otherwise. */
  changedTint: "base" | "core" | null;
  selectedLine: boolean;
  selectedSelection: boolean;
  /** A selection row of the active line: the deepest blue under the pointer. */
  hoverSelected: boolean;
  /** A selection row of another line: grey under the pointer. */
  hoverNotSelected: boolean;
  deleted: boolean;
  noBorderBottom: boolean;
}

/**
 * `getComputedRowClassNames` (`lineUtils.ts:12-81`) as facts, not classes:
 * the changed tint only with the switch on; the active line's rows marked, and
 * the active selection's own selection row; the hover steps on selection rows;
 * every row of a deleted line struck, and a deleted selection's own row; the
 * bottom rule dropped on a header row, and on a line-level row whose line has
 * notes or is `specific` (its notes row draws the rule instead).
 */
export function editorRowState(facts: {
  kind: EditorStateKind;
  line: EditorLineFacts;
  selection: EditorSelectionFacts | undefined;
  specType: SpecType;
  showChanges: boolean;
  active: ActiveRowIds | null;
}): EditorRowState {
  const { kind, line, selection, specType, showChanges, active } = facts;
  const changed = isEditorRowChanged(kind, line, selection, specType) && showChanges;
  const selectedLine = active?.lineId === line.id;
  const selectionRow = kind === "SELECTION_ONLY";
  const lineHasNotes = Boolean(line.notes) || line.locationType === "specific";
  return {
    changedTint: changed ? (specType === "community" ? "base" : "core") : null,
    selectedLine,
    selectedSelection:
      selectedLine &&
      selectionRow &&
      selection !== undefined &&
      active?.selectionId === selection.id,
    hoverSelected: selectedLine && selectionRow,
    hoverNotSelected: !selectedLine && selectionRow,
    deleted: line.deleted || (selectionRow && Boolean(selection?.deleted)),
    noBorderBottom: (lineHasNotes || kind === "LINE_ONLY") && !selectionRow && kind !== "NOTES",
  };
}

/**
 * The Active selection's cached row: the line and selection the pair names,
 * from the lines the page holds. A pair whose line or named selection is no
 * longer held resolves to none — the source's stored snapshot, without a
 * second copy to drift (#809 Assumption 3). #811 reads its payload from here.
 */
export function resolveActiveRow<
  S extends { id: number },
  L extends { id: number; selections: readonly S[] },
>(lines: Iterable<L>, active: ActiveRowIds | null): { line: L; selection: S | null } | null {
  if (!active) return null;
  for (const line of lines) {
    if (line.id !== active.lineId) continue;
    if (active.selectionId === null) return { line, selection: null };
    const selection = line.selections.find(({ id }) => id === active.selectionId);
    return selection ? { line, selection } : null;
  }
  return null;
}
